import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  SimpleGrid,
  Badge,
  Progress,
  useColorModeValue,
  Select,
  Flex,
  Spinner,
} from '@chakra-ui/react';
import { FiUsers, FiUserCheck, FiUserX, FiClock, FiCalendar } from 'react-icons/fi';

const AttendanceOverview = ({ 
  attendanceData, 
  weeklyData, 
  isLoading, 
  weeklyIsLoading 
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  // Use processed data from props instead of fetching
  const currentData = attendanceData || {};
  const weeklyAttendanceData = weeklyData || {};
  
  // Get color for attendance percentage
  const getAttendanceColor = (percentage) => {
    if (percentage >= 90) return 'green';
    if (percentage >= 75) return 'yellow';
    return 'red';
  };

  return (
    <Box>
      {/* Header with period selector */}
      <HStack justify="space-between" mb={6}>
        <Box>
          <Heading size="md" color="gray.900" fontWeight="600">
            Attendance Overview
          </Heading>
          <Text color="gray.600" fontSize="sm">
            {selectedPeriod === 'today' ? 'Today\'s attendance summary' : 'Weekly attendance trends'}
          </Text>
        </Box>
        <Select
          value={selectedPeriod}
          onChange={(e) => setSelectedPeriod(e.target.value)}
          size="sm"
          width="150px"
        >
          <option value="today">Today</option>
          <option value="week">This Week</option>
        </Select>
      </HStack>

      {selectedPeriod === 'today' ? (
        // Today's Overview
        <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 3, xl: 6 }} spacing={6}>
          {/* Total Employees */}
          <StatCard
            label="Total Employees"
            value={currentData.totalEmployees || 0}
            icon={<FiUsers />}
            color="blue"
            isLoading={isLoading}
          />

          {/* Present Employees */}
          <StatCard
            label="Present"
            value={currentData.presentCount || 0}
            icon={<FiUserCheck />}
            color="green"
            isLoading={isLoading}
          />

          {/* Absent Employees */}
          <StatCard
            label="Absent"
            value={currentData.absentCount || 0}
            icon={<FiUserX />}
            color="red"
            isLoading={isLoading}
          />

          {/* On Leave */}
          <StatCard
            label="On Leave"
            value={currentData.leaveCount || 0}
            icon={<FiCalendar />}
            color="orange"
            isLoading={isLoading}
          />

          {/* On Break */}
          <StatCard
            label="On Break"
            value={currentData.onBreakCount || 0}
            icon={<FiClock />}
            color="purple"
            isLoading={isLoading}
          />

          {/* Late Employees */}
          <StatCard
            label="Late"
            value={currentData.lateCount || 0}
            icon={<FiClock />}
            color="yellow"
            isLoading={isLoading}
          />
        </SimpleGrid>
      ) : (
                 // Weekly Overview
         <Box>
           {weeklyIsLoading ? (
             <Flex justify="center" p={8}>
               <Spinner size="lg" />
             </Flex>
           ) : (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
              {weeklyAttendanceData.weeklyData?.map((day, index) => (
                <Box
                  key={index}
                  bg={cardBg}
                  p={4}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={borderColor}
                  shadow="sm"
                >
                  <Text fontWeight="600" fontSize="sm" color="gray.700" mb={2}>
                    {day.day}
                  </Text>
                  <VStack spacing={2} align="start">
                                         <HStack justify="space-between" w="full">
                       <Text fontSize="xs" color="green.600">Present</Text>
                       <Badge colorScheme="green" variant="subtle" fontSize="xs">
                         {day.present}
                       </Badge>
                     </HStack>
                     <HStack justify="space-between" w="full">
                       <Text fontSize="xs" color="red.600">Absent</Text>
                       <Badge colorScheme="red" variant="subtle" fontSize="xs">
                         {day.absent}
                       </Badge>
                     </HStack>
                     <HStack justify="space-between" w="full">
                       <Text fontSize="xs" color="orange.600">Leave</Text>
                       <Badge colorScheme="orange" variant="subtle" fontSize="xs">
                         {day.leave}
                       </Badge>
                     </HStack>
                     <HStack justify="space-between" w="full">
                       <Text fontSize="xs" color="yellow.600">Late</Text>
                       <Badge colorScheme="yellow" variant="subtle" fontSize="xs">
                         {day.late}
                       </Badge>
                     </HStack>
                  </VStack>
                </Box>
              ))}
            </SimpleGrid>
          )}
        </Box>
      )}

      {/* Attendance Percentage Progress */}
      {selectedPeriod === 'today' && (
        <Box
          bg={cardBg}
          p={6}
          borderRadius="lg"
          border="1px solid"
          borderColor={borderColor}
          shadow="sm"
          mt={6}
        >
          <HStack justify="space-between" mb={3}>
            <Text fontWeight="600" color="gray.700">
              Overall Attendance Rate
            </Text>
            <Badge colorScheme={getAttendanceColor(currentData.attendancePercentage || 0)} variant="subtle">
              {currentData.attendancePercentage || 0}%
            </Badge>
          </HStack>
          <Progress
            value={currentData.attendancePercentage || 0}
            colorScheme={getAttendanceColor(currentData.attendancePercentage || 0)}
            size="lg"
            borderRadius="full"
          />
          <Text fontSize="sm" color="gray.600" mt={2}>
            {currentData.presentCount || 0} out of {currentData.totalEmployees || 0} employees present today
          </Text>
        </Box>
      )}
    </Box>
  );
};

// Stat Card Component
const StatCard = ({ label, value, icon, color, isLoading }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  const colorSchemes = {
    blue: { bg: 'blue.50', color: 'blue.600', border: 'blue.200' },
    green: { bg: 'green.50', color: 'green.600', border: 'green.200' },
    red: { bg: 'red.50', color: 'red.600', border: 'red.200' },
    orange: { bg: 'orange.50', color: 'orange.600', border: 'orange.200' },
    purple: { bg: 'purple.50', color: 'purple.600', border: 'purple.200' },
    yellow: { bg: 'yellow.50', color: 'yellow.600', border: 'yellow.200' },
  };

  const scheme = colorSchemes[color] || colorSchemes.blue;

  return (
    <Box
      bg={cardBg}
      p={6}
      borderRadius="lg"
      border="1px solid"
      borderColor={borderColor}
      shadow="sm"
      position="relative"
      overflow="hidden"
    >
      <HStack spacing={4}>
        <Box
          p={3}
          borderRadius="12px"
          bg={scheme.bg}
          color={scheme.color}
        >
          {icon}
        </Box>
        <VStack align="start" spacing={1}>
          <Text fontSize="sm" color="gray.600" fontWeight="500">
            {label}
          </Text>
          {isLoading ? (
            <Spinner size="sm" />
          ) : (
            <Text fontSize="2xl" fontWeight="700" color="gray.900">
              {value}
            </Text>
          )}
        </VStack>
      </HStack>
    </Box>
  );
};

export default AttendanceOverview; 